import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { RawIngestionEvent, IngestionEventType } from "@/components/admin/types";

// Transform database row to RawIngestionEvent type
function transformEvent(row: Record<string, unknown>): RawIngestionEvent {
  return {
    id: row.id as string,
    ingestion_id: row.ingestion_id as string,
    event_type: row.event_type as IngestionEventType,
    payload: row.payload as Record<string, unknown> | null,
    created_at: row.created_at as string,
    created_by: row.created_by as string | null,
  };
}

// Fetch all events for an ingestion
export function useIngestionEvents(ingestionId: string | null) {
  return useQuery({
    queryKey: ["ingestion-events", ingestionId],
    queryFn: async () => {
      if (!ingestionId) return [];
      const { data, error } = await supabase
        .from("raw_ingestion_events")
        .select("*")
        .eq("ingestion_id", ingestionId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data || []).map(row => transformEvent(row as Record<string, unknown>));
    },
    enabled: !!ingestionId,
  });
}

// Log a new event
export function useLogIngestionEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      ingestionId,
      eventType,
      payload,
      userId,
    }: {
      ingestionId: string;
      eventType: IngestionEventType;
      payload?: Record<string, unknown> | null;
      userId?: string | null;
    }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.from("raw_ingestion_events") as any)
        .insert({
          ingestion_id: ingestionId,
          event_type: eventType,
          payload: payload ?? null,
          created_by: userId ?? null,
        })
        .select()
        .single();

      if (error) throw error;
      return transformEvent(data as Record<string, unknown>);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["ingestion-events", data.ingestion_id] });
    },
  });
}
